import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCreateFeatureUpdate } from '../../hooks/useIdeaBoard';

const MIN_CHARS = 10;
const MAX_CHARS = 2000;

interface AdminFeatureUpdateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AdminFeatureUpdateModal: React.FC<AdminFeatureUpdateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [summary, setSummary] = useState('');
  const [validationError, setValidationError] = useState('');

  const createMutation = useCreateFeatureUpdate();
  const { reset: resetCreateMutation } = createMutation;

  useEffect(() => {
    if (open) {
      setSummary('');
      setValidationError('');
      resetCreateMutation();
    }
  }, [open, resetCreateMutation]);

  const handleClose = () => {
    if (!createMutation.isPending) {
      onClose();
    }
  };

  const validate = (): boolean => {
    if (summary.trim().length < MIN_CHARS) {
      setValidationError(`Summary must be at least ${MIN_CHARS} characters.`);
      return false;
    }
    if (summary.trim().length > MAX_CHARS) {
      setValidationError(`Summary must not exceed ${MAX_CHARS} characters.`);
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    createMutation.mutate(
      { summary: summary.trim() },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      aria-labelledby="create-feature-update-dialog-title"
    >
      <DialogTitle id="create-feature-update-dialog-title">Create Feature Update</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Post a standalone feature update to let users know about improvements that have been made.
        </Typography>

        {createMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {createMutation.error?.message || 'Failed to create feature update. Please try again.'}
          </Alert>
        )}

        <TextField
          label="Feature Update Summary"
          multiline
          rows={4}
          fullWidth
          value={summary}
          onChange={(e) => {
            setSummary(e.target.value);
            if (validationError) setValidationError('');
          }}
          error={!!validationError}
          helperText={validationError || `${summary.length.toLocaleString()} / ${MAX_CHARS.toLocaleString()} characters`}
          inputProps={{ maxLength: MAX_CHARS }}
          disabled={createMutation.isPending}
          placeholder="Describe the new feature or improvement..."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMutation.isPending || summary.trim().length < MIN_CHARS}
          startIcon={createMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {createMutation.isPending ? 'Creating...' : 'Create Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminFeatureUpdateModal;
